// Pure color math shared by the plan renderer and the company-wide allocator.
const HUES = [0, 20, 80, 108, 136, 164, 192, 220, 248, 276, 304, 332];
const LEGACY_COLOR_TEXT = {
    '#cfe8d8': '#21653d', '#cbdff7': '#245a9a', '#e4d7f5': '#623a91', '#f5d1ce': '#9f2922',
    '#bcebdc': '#20654f', '#c9e7e5': '#226c68', '#dfecc1': '#42620a', '#f3d2e7': '#932b72',
    '#efc6b5': '#8a402b', '#f4d7c1': '#9a4b12', '#d8d2ef': '#4c3d8c', '#cce4ef': '#20627b'
};
const RESERVED_REPLACEMENTS = {'#f7e2ad': '#bcebdc', '#d7dde3': '#efc6b5'};

function hslHex(hue, saturation, lightness) {
    const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
    const x = chroma * (1 - Math.abs((hue / 60) % 2 - 1));
    const m = lightness - chroma / 2;
    const output = [[chroma, x, 0], [x, chroma, 0], [0, chroma, x],
        [0, x, chroma], [x, 0, chroma], [chroma, 0, x]][Math.floor(hue / 60)];
    return '#' + output.map(channel => Math.round((channel + m) * 255).toString(16).padStart(2, '0')).join('');
}

const PALETTE = Object.freeze(HUES.map(hue => hslHex(hue, 0.6, 0.82)));
// Manual choices add vivid and dark shades without changing automatic allocation.
const MANUAL_PALETTE = Object.freeze(PALETTE.concat(
    HUES.map(hue => hslHex(hue, 0.85, 0.52)),
    HUES.map(hue => hslHex(hue, 0.65, 0.25))
));
const COLOR_TEXT = Object.freeze(Object.assign({}, LEGACY_COLOR_TEXT,
    ...HUES.map((hue, index) => ({[PALETTE[index]]: hslHex(hue, 0.6, 0.25)})),
    ...MANUAL_PALETTE.slice(PALETTE.length).map(color => ({[color]:
        contrastRatio(color, '#000000') >= contrastRatio(color, '#ffffff') ? '#000000' : '#ffffff'}))));
const ALLOWED_COLORS = new Set(Object.keys(COLOR_TEXT));

function normalizeTargetColor(value) {
    const normalized = String(value || '').trim().toLowerCase();
    return RESERVED_REPLACEMENTS[normalized] || normalized;
}
function isTargetColor(value) { return ALLOWED_COLORS.has(String(value || '').trim().toLowerCase()); }
function rgb(hex) {
    return /^#[\da-f]{6}$/i.test(hex)
        ? [1, 3, 5].map(index => parseInt(hex.slice(index, index + 2), 16) / 255) : null;
}
function linear(channel) { return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4; }
function contrastRatio(background, foreground) {
    const luminance = hex => {
        const channels = rgb(hex).map(linear);
        return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
    };
    const first = luminance(background), second = luminance(foreground);
    return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
}
function normalizeContrast(value) {
    const number = Number(value);
    return Number.isFinite(number) ? Math.round(Math.min(100, Math.max(0, number)) / 5) * 5 : 0;
}
function contrastFill(background, contrast) {
    const amount = normalizeContrast(contrast) / 100;
    const channels = rgb(background);
    // White marks unassigned/empty cells and must not turn into the grey rest state.
    if (!amount || !channels || channels.every(channel => channel === 1)) return background;
    const [r, g, b] = channels;
    const max = Math.max(r, g, b), min = Math.min(r, g, b), delta = max - min;
    let lightness = (max + min) / 2;
    let saturation = delta ? delta / (1 - Math.abs(2 * lightness - 1)) : 0;
    let hue = 0;
    if (delta) {
        hue = max === r ? ((g - b) / delta) % 6 : max === g ? (b - r) / delta + 2 : (r - g) / delta + 4;
        hue = (hue * 60 + 360) % 360;
        saturation += (1 - saturation) * amount;
    }
    lightness += (0.5 - lightness) * amount;
    return hslHex(hue, saturation, lightness);
}
function colorPresentation(background, foreground, contrast) {
    if (!normalizeContrast(contrast) || !rgb(background)) return {backgroundColor: background, color: foreground};
    const fill = contrastFill(background, contrast);
    const text = rgb(foreground) && contrastRatio(fill, foreground) >= 4.5 ? foreground
        : contrastRatio(fill, '#000000') >= contrastRatio(fill, '#ffffff') ? '#000000' : '#ffffff';
    return {backgroundColor: fill, color: text};
}
function oklab(hex) {
    const [r, g, b] = rgb(hex).map(linear);
    const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
    const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
    const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
    return [0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
        1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
        0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s];
}
function colorDistance(first, second) {
    return Math.min(...[0, 35, 100].map(contrast => {
        const a = oklab(contrastFill(first, contrast)), b = oklab(contrastFill(second, contrast));
        return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
    }));
}

module.exports = {PALETTE, MANUAL_PALETTE, COLOR_TEXT, normalizeTargetColor, isTargetColor, normalizeContrast,
    contrastFill, contrastRatio, colorPresentation, colorDistance, hslHex};
